import React from 'react';
import { Layout, Typography, List, Divider, Anchor, Card, Collapse } from 'antd';
import { Link } from 'react-router-dom';

const { Title, Paragraph, Text } = Typography;
const { Content } = Layout;
const { Panel } = Collapse;
const { Link: AnchorLink } = Anchor;

const HelpPage = () => {
  const sidebarNavigation = [
    { title: 'Estates', description: 'View, create, and manage all estates.', link: '/estates' },
    { title: 'Family Jewels', description: 'Track and manage key family assets.', link: '/family-jewels' },
    { title: 'Probate Docs', description: 'Prepare court documents from probate templates.', link: '/probate-docs' },
    { title: 'Calendar', description: 'Schedule and view important events.', link: '/calendar' },
    { title: 'Family Tree', description: 'Visualize and manage the family structure.', link: '/family-tree' },
  ];

  const quickLinks = [
    { title: 'Assets', description: 'Add, view, and manage estate assets.', link: '/assets' },
    { title: 'Liabilities', description: 'List and track liabilities.', link: '/liabilities' },
    { title: 'Cashflows', description: 'Analyze estate-related cash flows.', link: '/cashflows' },
    { title: 'Participants', description: 'Manage participants involved in estates.', link: '/participants' },
  ];

  const actions = [
    {
      title: 'Add a New Estate', steps: [
        'Go to the Estates section from the sidebar.',
        'Click the Add Estate button.',
        'Fill in the details and save.',
      ]
    },
    {
      title: 'Review Source Evidence', steps: [
        'Open Source Documents and confirm the active estate.',
        'Upload evidence, then review extracted information against the original document.',
        'Confirm the records to save. Source evidence is separate from the court templates in Probate Docs.',
      ]
    },
    {
      title: 'Add an Asset', steps: [
        'Open the Assets section from the sidebar.',
        'Click the Add Asset button.',
        'Enter details and associate it with an estate.',
      ]
    },
    {
      title: 'Schedule an Event', steps: [
        'Go to the Calendar section.',
        'Click the Add Event button.',
        'Enter the details and save.',
      ]
    },
  ];

  const valuationConcepts = [
    {
      title: 'How an asset gets its value',
      content: <>
        <Paragraph>
          Every asset has a <Text strong>valuation basis</Text>, which says where its
          value comes from. It describes the <Text italic>source</Text> of the figure,
          not what kind of thing the asset is -  a bank balance, a life insurance
          policy and a dealer's quote for a shotgun are all valued the same way,
          because in each case somebody external states the amount.
        </Paragraph>
        <List
          size="small"
          dataSource={[
            'Market quoted - shares, funds, bonds. Worked out from quantity and the latest price, so it moves with the market. You enter the quantity and price, not the value.',
            'Attested - a bank balance, an insurance policy amount, a dealer quote. A third party states the figure, so it is used exactly as given.',
            'Appraised - Kelly Blue Book, an auction estimate, a valuer’s opinion. An opinion, so this is where a market discount normally belongs.',
            'Statement - a pension administrator’s figure, or an annual statement.',
          ]}
          renderItem={(item) => <List.Item>{item}</List.Item>}
        />
        <Paragraph>
          The asset type suggests a basis when you pick one, and you can change it.
          Type alone cannot decide it: a life insurance policy and a holding of shares
          are both financial assets, but a death benefit is a stated amount while
          shares are quantity times price.
        </Paragraph>
      </>,
    },
    {
      title: 'What you enter, and what is worked out for you',
      content: <>
        <Paragraph><Text strong>You enter:</Text></Paragraph>
        <List
          size="small"
          dataSource={[
            'Value - the gross amount before any discount (or quantity and price, for market-quoted assets)',
            'Discount to market (%) - how far below the stated value the asset would really sell for',
            'Discount to family (%) - the haircut applied when passing an asset to family',
            'Liens - any loan or security interest secured against the asset',
            'Cash received - what actually came in when the asset was sold',
            'Flags - covered in will, jointly owned, sold, small estate, lease',
          ]}
          renderItem={(item) => <List.Item>{item}</List.Item>}
        />
        <Paragraph><Text strong>Worked out for you:</Text></Paragraph>
        <Paragraph type="secondary">
          Market value, estimated value, net of lien, internal distribution amount,
          realised external sale, estate value, counted in probate, jointly owned
          value, exempt amount, small estate value, family discounted value, estate
          left to be distributed, and variance from estimate.
        </Paragraph>
        <Paragraph>
          These are recalculated every time they are shown, so they always reflect
          what is currently entered. You cannot edit them directly - change the
          figures they are built from instead.
        </Paragraph>
      </>,
    },
    {
      title: 'Entering percentages',
      content: <>
        <Paragraph>
          Discounts are entered as percentages between 0 and 100.
          Enter <Text code>30</Text> for a 30% discount - not <Text code>0.3</Text>.
        </Paragraph>
        <Paragraph type="secondary">
          A 30% discount on a $10,000 appraisal gives $7,000. Entering 0.3 by mistake
          would give $9,970, which looks plausible and is easy to miss.
        </Paragraph>
      </>,
    },
  ];

  const assetComputationSections = [
    {
      title: '1. Market Value',
      content: <>
        <Paragraph>For a market-quoted asset:</Paragraph>
        <Text code>Market Value = Current Quantity Owned × Prior Day Closing Price</Text>
        <Paragraph>Example: <Text type="secondary">400 shares × $165.51 = $66,204</Text></Paragraph>
        <Paragraph>For every other basis, the market value is the value you entered.</Paragraph>
        <Paragraph type="secondary">
          Cost basis is what a holding was paid for, and is not used as its value.
          It is kept so gain or loss can be worked out.
        </Paragraph>
      </>,
    },
    {
      title: '2. Estimated Value',
      content: <>
        <Text code>Estimated Value = Net of Lien × (1 - Discount to Market ÷ 100)</Text>
        <Paragraph>Example: <Text type="secondary">$450,000 × (1 - 10 ÷ 100) = $405,000</Text></Paragraph>
        <Paragraph>
          The discount comes off the value remaining after any lien, so an encumbered
          asset is discounted on what is actually left of it.
        </Paragraph>
        <Paragraph type="secondary">
          A quoted share price or a bank balance is already the value, so these
          normally carry no discount - but a discount entered against one is applied.
        </Paragraph>
      </>,
    },
    {
      title: '3. Net of Lien',
      content: <>
        <Paragraph>Where the asset is leased, any lien is deducted:</Paragraph>
        <Text code>Net of Lien = Market Value - Liens</Text>
        <Paragraph>Example: <Text type="secondary">$5,400 - $1,000 = $4,400</Text></Paragraph>
        <Paragraph>Otherwise it is the full market value.</Paragraph>
      </>,
    },
    {
      title: '4. Internal Distribution Amount',
      content: <>
        <Paragraph>Where the asset is distributed internally for cash:</Paragraph>
        <Text code>Internal Distribution Amount = Estimated Value × (1 - Discount to Family ÷ 100)</Text>
        <Paragraph>Example: <Text type="secondary">$4,000 × (1 - 10 ÷ 100) = $3,600</Text></Paragraph>
        <Paragraph>Otherwise it is zero.</Paragraph>
      </>,
    },
    {
      title: '5. Realised External Sale',
      content: <>
        <Paragraph>
          An asset is either sold for cash or distributed in kind, never both, so
          only one figure counts:
        </Paragraph>
        <Text code>Realised External Sale = Cash Received, or the Internal Distribution Amount where nothing was sold</Text>
        <Paragraph>Example: <Text type="secondary">Sold for $1,000 → $1,000, whatever the internal distribution figure says</Text></Paragraph>
        <Paragraph>A cashless internal distribution realises nothing, so this is zero.</Paragraph>
      </>,
    },
    {
      title: '6. Estate Value and Counted in Probate',
      content: <>
        <Paragraph>Where the asset is covered in the will:</Paragraph>
        <Text code>Estate Value = Estimated Value</Text><br />
        <Text code>Counted in Probate = 0</Text>
        <Paragraph>Otherwise:</Paragraph>
        <Text code>Counted in Probate = Estimated Value</Text>
      </>,
    },
    {
      title: '7. Jointly Owned Value',
      content: <>
        <Paragraph>Where the asset is jointly owned:</Paragraph>
        <Text code>Jointly Owned Value = Market Value</Text>
      </>,
    },
    {
      title: '8. Family Discounted Value',
      content: <>
        <Paragraph>What an asset is worth after the family haircut:</Paragraph>
        <Text code>Family Discounted Value = Estimated Value × (1 - Discount to Family ÷ 100)</Text>
        <Paragraph>Example: <Text type="secondary">$405,000 × (1 - 30 ÷ 100) = $283,500</Text></Paragraph>
        <Paragraph>
          Applies to every asset. The haircut reflects how an item is passed on, not
          how it is owned.
        </Paragraph>
      </>,
    },
    {
      title: '9. Probate and Ownership',
      content: <>
        <Paragraph>Property named in a will or trust is excluded from the probate figure:</Paragraph>
        <Text code>Counted in Probate = 0 where covered in a will or trust, otherwise Estimated Value</Text><br />
        <Text code>Estate Value = Estimated Value where covered in a will or trust</Text><br />
        <Text code>Jointly Owned Value = Market Value where ownership passes at death</Text>
        <Paragraph type="secondary">
          Joint ownership, a trust, or a named beneficiary means an asset passes
          directly rather than through the probate administration.
        </Paragraph>
      </>,
    },
    {
      title: '10. Share of the Estate',
      content: <>
        <Paragraph>Each asset measured against the estate total:</Paragraph>
        <Text code>Share at Full Value = Estimated Value ÷ Total Estimated Value</Text><br />
        <Text code>Share after Discount = Family Discounted Value ÷ Total Estimated Value</Text>
        <Paragraph>Example: <Text type="secondary">$75,000 of a $100,000 estate = 0.75</Text></Paragraph>
        <Paragraph>
          Assets distributed for cash are excluded from the non-cash share.
        </Paragraph>
      </>,
    },
    {
      title: '11. Small Estate Value',
      content: <>
        <Paragraph>
          Counts only where the asset is flagged for the small estate
          <Text strong> and</Text> its value is certain - attested or market quoted.
          Small estate procedures rely on amounts that are not in doubt, so an
          appraisal does not qualify however it is flagged.
        </Paragraph>
        <Text code>Small Estate Value = Market Value</Text>
        <Paragraph type="secondary">
          The undiscounted value is used here, not the estimated value.
        </Paragraph>
      </>,
    },
    {
      title: '12. Variance from Estimate',
      content: <>
        <Paragraph>How far the realised amount landed from the estimate:</Paragraph>
        <Text code>Variance % = (Realised External Sale - Estimated Value) ÷ Estimated Value × 100</Text>
        <Paragraph>Example: <Text type="secondary">($3,000 - $4,000) ÷ $4,000 × 100 = -25%</Text></Paragraph>
        <Paragraph>A negative figure means the asset realised less than estimated. An asset with no value shows 0.</Paragraph>
      </>,
    },
    {
      title: '13. Estate Left To Be Distributed',
      content: <>
        <Paragraph>Where the asset is marked as distributed:</Paragraph>
        <Text code>Estate Left To Be Distributed = Estimated Value</Text>
        <Paragraph>Otherwise it is zero.</Paragraph>
      </>,
    },
    {
      title: '14. Cost Basis and Gain or Loss',
      content: <>
        <Paragraph>For a holding with a portfolio:</Paragraph>
        <Text code>Cost Basis Amount = Cost Basis × Current Quantity Owned</Text><br />
        <Text code>Gain or Loss = Market Value - Cost Basis Amount</Text>
        <Paragraph>
          Example: <Text type="secondary">400 shares bought at $6.93 cost $2,772; at $165.51 they are worth $66,204, a gain of $63,432</Text>
        </Paragraph>
      </>,
    },
  ];

  const exemptPropertySections = [
    {
      title: 'What exempt property is',
      content: <>
        <Paragraph>
          Certain household and personal items are set aside for the immediate family
          and do not form part of the probate estate. This reduces the value that
          passes through probate.
        </Paragraph>
        <Paragraph>
          Real estate is never exempt - the rules cover personal property only.
        </Paragraph>
      </>,
    },
    {
      title: 'Who has to survive for it to apply',
      content: <>
        <Paragraph>There must be either:</Paragraph>
        <List
          size="small"
          dataSource={[
            'a surviving spouse, or',
            'a child who was under 21 at the date of death',
          ]}
          renderItem={(item) => <List.Item>{item}</List.Item>}
        />
        <Paragraph>
          With neither, nothing is exempt. This is worked out from the beneficiaries
          recorded against the estate, so adding a beneficiary or filling in a date of
          birth may change the result. The Family Jewels page says which of these it
          could not determine.
        </Paragraph>
      </>,
    },
    {
      title: 'Categories and limits',
      content: <>
        <Paragraph>
          Each category has its own limit. The limit applies to the
          <Text strong> estate&apos;s total for that category</Text>, not to each item, so
          several small items can reach it together.
        </Paragraph>
        <List
          size="small"
          dataSource={[
            'Household goods, furniture, clothing, jewellery, utensils, electronics - $20,000',
            'Books, family pictures, religious books, software and media - $2,500',
            'Farm and domestic animals with 60 days’ feed, machinery, one tractor, one lawn tractor - $20,000',
            'One motor vehicle - $25,000',
            'Money: cash, bank accounts, certificates of deposit, marketable securities - $25,000',
            'Set-off for a child under 21 - $10,000',
          ]}
          renderItem={(item) => <List.Item>{item}</List.Item>}
        />
        <Paragraph type="secondary">Up to $102,500 across all categories.</Paragraph>
      </>,
    },
    {
      title: 'How the limit is applied',
      content: <>
        <Paragraph>For each category:</Paragraph>
        <Text code>Claimed = total value of the assets in that category, less any liens</Text><br />
        <Text code>Exempt = the lower of Claimed and the limit</Text><br />
        <Text code>Excess = anything above the limit, which stays in the probate estate</Text>
        <Paragraph>
          Example: <Text type="secondary">
            $35,000 of household goods against a $20,000 limit - $20,000 is set aside
            and $15,000 remains in the estate.
          </Text>
        </Paragraph>
        <Paragraph>
          Where a category is over its limit, each asset in it keeps a share of the
          exempt amount in proportion to its value.
        </Paragraph>
      </>,
    },
    {
      title: 'Which state applies',
      content: <>
        <Paragraph>
          The rules come from the state recorded on the estate - the state the
          deceased lived in, not where each asset sits. Set it on the estate for the
          exemption to be worked out.
        </Paragraph>
        <Paragraph type="secondary">
          New York is currently configured. Estates in other states show a notice and
          no exemption is applied.
        </Paragraph>
      </>,
    },
  ];

  const glossary = [
    { term: 'Valuation basis', meaning: 'Where an asset’s value comes from: market quoted, attested, appraised or statement.' },
    { term: 'Value', meaning: 'The gross amount before any discount. For market-quoted assets this is worked out from quantity and price instead of entered.' },
    { term: 'Market value', meaning: 'What the asset is worth now. For quoted holdings, quantity times the latest price.' },
    { term: 'Cost basis', meaning: 'What a holding was originally paid for, per unit. Used for gain or loss, never as the value.' },
    { term: 'Estimated value', meaning: 'Market value after the market discount. Only appraised assets are discounted.' },
    { term: 'Discount to market', meaning: 'How far below the stated value an appraised asset would really sell for, as a percentage.' },
    { term: 'Discount to family', meaning: 'The haircut applied when an asset passes to family rather than being sold, as a percentage.' },
    { term: 'Lien', meaning: 'A loan or security interest secured against the asset. Reduces the value for exemption purposes.' },
    { term: 'Probate estate', meaning: 'The gross estate less exempt property - what actually passes through probate.' },
    { term: 'Exempt property', meaning: 'Household and personal items set aside for the immediate family, outside the probate estate.' },
    { term: 'Attested', meaning: 'A figure stated by a third party such as a bank, insurer or dealer.' },
    { term: 'Appraised', meaning: 'A figure that is somebody’s estimate, such as an auction estimate or valuer’s opinion.' },
  ];

  return (
    <Content className="help-page" style={{ padding: '2rem', background: '#fff' }}>
      <div style={{ display: 'flex', gap: '2rem' }}>
        <div style={{ flex: 1 }}>
          <Title level={2}>Help Center</Title>
          <Paragraph>Prepare a court document using confirmed estate records and reviewed source evidence.</Paragraph>
          <ol>
            <li><Link to="/source-documents">Review source documents</Link> against the original evidence, then confirm the records to save.</li>
            <li><Link to="/filings/court-figures">Review court figures</Link> and follow missing-value warnings back to the shared records.</li>
            <li><Link to="/probate-docs">Open probate documents</Link>, select a template, answer its filing questions and inspect the filled PDF.</li>
          </ol>
          <Paragraph>Source Documents contains estate evidence. Probate Docs contains court templates and their filled previews. Review signing, notary and court blanks before completing the filing.</Paragraph>

          <Divider />
          <Title level={3} id="sidebar-navigation">Sidebar Navigation</Title>
          <List
            itemLayout="vertical"
            dataSource={sidebarNavigation}
            renderItem={({ title, description, link }) => (
              <List.Item>
                <Card type="inner" title={<Link to={link}>{title}</Link>}>
                  {description}
                </Card>
              </List.Item>
            )}
          />

          <Divider />
          <Title level={3} id="quick-links">Quick Links</Title>
          <List
            itemLayout="vertical"
            dataSource={quickLinks}
            renderItem={({ title, description, link }) => (
              <List.Item>
                <Card type="inner" title={<Link to={link}>{title}</Link>}>
                  {description}
                </Card>
              </List.Item>
            )}
          />

          <Divider />
          <Title level={3} id="common-tasks">How to Perform Common Tasks</Title>
          <Collapse accordion>
            {actions.map(({ title, steps }) => (
              <Panel header={title} key={title}>
                <List size="small" dataSource={steps} renderItem={(step) => <List.Item>{step}</List.Item>} />
              </Panel>
            ))}
          </Collapse>

          <Divider />
          <Title level={3} id="understanding-values">Understanding Asset Values</Title>
          <Paragraph>
            Start here if you are unsure which figure to enter where, or why a value
            on screen is not the one you typed.
          </Paragraph>
          <Collapse accordion>
            {valuationConcepts.map(({ title, content }) => (
              <Panel header={title} key={title}>{content}</Panel>
            ))}
          </Collapse>

          <Divider />
          <Title level={3} id="asset-computations">Asset Value Computations</Title>
          <Paragraph>
            Each figure below is worked out automatically whenever it is shown.
          </Paragraph>
          <Collapse accordion>
            {assetComputationSections.map(({ title, content }) => (
              <Panel header={title} key={title}>{content}</Panel>
            ))}
          </Collapse>

          <Divider />
          <Title level={3} id="exempt-property">Exempt Property</Title>
          <Paragraph>
            Property set aside for the immediate family, which does not pass through
            probate.
          </Paragraph>
          <Collapse accordion>
            {exemptPropertySections.map(({ title, content }) => (
              <Panel header={title} key={title}>{content}</Panel>
            ))}
          </Collapse>
          <Paragraph type="secondary" style={{ marginTop: '1rem' }}>
            Statutory amounts change by amendment. These figures support preparation
            and review; confirm them with your attorney before relying on them in a
            filing.
          </Paragraph>

          <Divider />
          <Title level={3} id="glossary">Glossary</Title>
          <List
            itemLayout="vertical"
            dataSource={glossary}
            renderItem={({ term, meaning }) => (
              <List.Item>
                <Text strong>{term}</Text>: {meaning}
              </List.Item>
            )}
          />

          <Divider />
          <Title level={3} id="faqs">FAQs</Title>
          <List
            itemLayout="vertical"
            dataSource={[
              { question: 'How do I reset my password?', answer: 'Go to the Account section, select "Change Password," and follow the instructions.' },
              { question: 'Can I delete an estate?', answer: 'Yes, go to the Estates section, select the estate, and choose the delete option.' },
              { question: 'How do I log out?', answer: 'Click the Logout option in the sidebar at the bottom.' },
            ]}
            renderItem={({ question, answer }) => (
              <List.Item>
                <Card type="inner" title={question}>{answer}</Card>
              </List.Item>
            )}
          />

          <Divider />
          <Title level={3} id="contact">Contact Support</Title>
          <Paragraph>
            If you have additional questions or face any issues, please contact our support team through the Help Center or email us at <Text strong>peterfx@wzee.net</Text>.
          </Paragraph>
        </div>

        <div style={{ width: '260px' }}>
          <Anchor offsetTop={100} affix>
            <AnchorLink href="#sidebar-navigation" title="Sidebar Navigation" />
            <AnchorLink href="#quick-links" title="Quick Links" />
            <AnchorLink href="#common-tasks" title="Common Tasks" />
            <AnchorLink href="#understanding-values" title="Understanding Asset Values" />
            <AnchorLink href="#asset-computations" title="Asset Computations" />
            <AnchorLink href="#exempt-property" title="Exempt Property" />
            <AnchorLink href="#glossary" title="Glossary" />
            <AnchorLink href="#faqs" title="FAQs" />
            <AnchorLink href="#contact" title="Contact Support" />
          </Anchor>
        </div>
      </div>
    </Content>
  );
};

export default HelpPage;
